import { useApplicationContainer } from 'containers/ApplicationContainer';
import { CANCELLED, POSTPONED } from 'constants/GameSecondaryStatuses';
import { COMPLETED } from 'constants/GameStatuses';

export interface Team {
  name: string;
  webPath: string;
  webSchedulePath?: string;
  image?: string;
  sport?: { name: string };
  gender?: { name: string };
  genderSport?: {
    name?: string;
    sport?: { name: string };
    gender?: { name: string };
  };
}

export interface Game {
  titleText: string;
  date: string;
  locationText?: string;
  webPath: string;
  statusId: number;
  secondaryStatusId?: number;
  contest?: {
    gofanTicketingUrl?: string;
    nfhsNetworkUrl?: string;
  };
  featured?: { team: Team; isHome: boolean };
  opponent?: { team: Team; isHome: boolean };
  gameTeams?: { team: Team }[];
}

export function getGameEventStatus(game: Game): string {
  if (game.secondaryStatusId === CANCELLED) {
    return 'https://schema.org/EventCancelled';
  } else if (game.secondaryStatusId === POSTPONED) {
    return 'https://schema.org/EventPostponed';
  } else if (game.statusId === COMPLETED) {
    return 'https://schema.org/EventEnded';
  }
  return 'https://schema.org/EventScheduled';
}

export function createTeamStructuredData(team: Team): any {
  return {
    '@type': 'SportsTeam',
    name: team.name,
    sport: team.sport?.name || team.genderSport?.sport?.name,
    gender: team.gender?.name || team.genderSport?.gender?.name,
    url: team.webSchedulePath ? addDomain(team.webSchedulePath) : addDomain(team.webPath),
    ...(team.image && {
      image: team.image
    })
  };
}

export function createGameStructuredData(game: Game, homeTeam: Team, awayTeam: Team): any {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    name: game.titleText,
    startDate: game.date,
    ...(game.locationText && {
      location: {
        '@type': 'Place',
        name: game.locationText
      }
    }),
    homeTeam: createTeamStructuredData(homeTeam),
    awayTeam: createTeamStructuredData(awayTeam),
    eventStatus: getGameEventStatus(game),
    url: addDomain(game.webPath),
    offers: [] as any[]
  };

  if (game.contest?.gofanTicketingUrl) {
    jsonLd.offers.push({
      '@type': 'Offer',
      description: 'Buy tickets to the game!',
      url: game.contest.gofanTicketingUrl,
      priceCurrency: 'USD',
      availability: 'http://schema.org/InStock',
      isFamilyFriendly: true
    });
  }

  if (game.contest?.nfhsNetworkUrl) {
    jsonLd.offers.push({
      '@type': 'Offer',
      description: 'Live stream the game!',
      url: game.contest.nfhsNetworkUrl,
      priceCurrency: 'USD',
      availability: 'http://schema.org/InStock',
      isFamilyFriendly: true
    });
  }

  return jsonLd;
}

export function createTeamProfileStructuredData(team: Team, games: Game[]): any {
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: {
      ...createTeamStructuredData(team),
      event: games.map(game => {
        const homeTeam = game.featured?.isHome ? game.featured?.team : game.opponent?.team;
        const awayTeam = game.featured?.isHome ? game.opponent?.team : game.featured?.team;

        // Ensure both teams are defined before creating structured data
        if (!homeTeam || !awayTeam) {
          return null;
        } else {
          return createGameStructuredData(game, homeTeam, awayTeam);
        }
      })
    }
  };
}

function addDomain(path: string): string {
  const [, { addDomain }] = useApplicationContainer();
  return addDomain(path);
}
